"use client"

import { useState, useEffect } from "react"
import { X, Calendar, Plus, Share2, Download, Edit3 } from "lucide-react"
import "./PlanningModal.css"

const PlanningModal = ({
  isOpen,
  onClose,
  places = [],
  currentListId,
  userId,
  userNickname,
  onTripCreated,
  API_BASE,
  linkedTripData,
  hasLinkedTrip,
}) => {
  console.log("🎯 PlanningModal render:", {
    isOpen,
    placesCount: places.length,
    currentListId,
    userId,
    userNickname,
    hasLinkedTrip,
    linkedTripData: !!linkedTripData,
    timestamp: new Date().toISOString(),
  })

  // Core state
  const [currentMode, setCurrentMode] = useState("view")
  const [itinerary, setItinerary] = useState([])
  const [accommodations, setAccommodations] = useState([])
  const [calendarName, setCalendarName] = useState("Trip Calendar")
  const [tripTimezone, setTripTimezone] = useState("America/Chicago")
  const [selectedDate, setSelectedDate] = useState(null)
  const [currentEditingPlanInfo, setCurrentEditingPlanInfo] = useState({
    originalDate: null,
    dayIndex: -1,
    planIndex: -1,
  })
  const [currentEditingAccommodationId, setCurrentEditingAccommodationId] = useState(null)

  // Sharing state
  const [isSharedPlan, setIsSharedPlan] = useState(false)
  const [sharedPlanId, setSharedPlanId] = useState(null)
  const [currentActiveLinkId, setCurrentActiveLinkId] = useState(null)
  const [canEdit, setCanEdit] = useState(true)

  // UI state
  const [showWelcome, setShowWelcome] = useState(true) // Start with welcome screen
  const [showPlanModal, setShowPlanModal] = useState(false)
  const [showAccommodationModal, setShowAccommodationModal] = useState(false)
  const [showNewTripModal, setShowNewTripModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null)

  const PLAN_ICONS = {
    accommodation: "🏠",
    meal: "🍽️",
    activity: "🎯",
    flight: "✈️",
    drive: "🚗",
    note: "📝",
  }

  // Load linked trip data when it changes
  useEffect(() => {
    console.log("📅 PlanningModal useEffect - linkedTripData changed:", {
      hasLinkedTripData: !!linkedTripData,
      hasLinkedTrip,
      itineraryLength: linkedTripData?.itinerary?.length || 0,
    })

    if (linkedTripData && linkedTripData.itinerary && linkedTripData.itinerary.length > 0) {
      console.log("📅 Loading existing trip data into modal")

      // Load the trip data into the modal state
      setItinerary(linkedTripData.itinerary || [])
      setAccommodations(linkedTripData.accommodations || [])
      setCalendarName(linkedTripData.calendarName || "Trip Calendar")
      setTripTimezone(linkedTripData.tripTimezone || "America/Chicago")
      setSharedPlanId(linkedTripData.sharedPlanId || null)
      setCurrentActiveLinkId(linkedTripData.currentActiveLinkId || null)
      setIsSharedPlan(!!linkedTripData.sharedPlanId)

      // Set initial selected date
      if (linkedTripData.itinerary.length > 0) {
        const today = new Date().toISOString().split("T")[0]
        const todayInTrip = linkedTripData.itinerary.find((day) => day.date === today)
        setSelectedDate(todayInTrip ? today : linkedTripData.itinerary[0].date)
      }

      // Show the planning interface instead of welcome
      setShowWelcome(false)
    } else {
      console.log("📅 No valid trip data, showing welcome screen")
      setShowWelcome(true)
      // Reset state for new trip
      setItinerary([])
      setAccommodations([])
      setCalendarName("Trip Calendar")
      setTripTimezone("America/Chicago")
      setSelectedDate(null)
    }
  }, [linkedTripData, hasLinkedTrip])

  // Debug logging for modal lifecycle
  useEffect(() => {
    console.log("🎯 PlanningModal state updated:", {
      isOpen,
      showWelcome,
      itineraryLength: itinerary.length,
      selectedDate,
      hasLinkedTrip,
    })
  }, [isOpen, showWelcome, itinerary.length, selectedDate, hasLinkedTrip])

  // Utility functions
  const formatDateForInput = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const generateId = () => {
    return "id_" + Math.random().toString(36).substring(2, 15)
  }

  // Save to localStorage and notify parent
  const saveToLocal = () => {
    try {
      const dataToSave = {
        calendarName: calendarName,
        itinerary: itinerary,
        accommodations: accommodations,
        tripTimezone: tripTimezone,
        sharedPlanId: sharedPlanId,
        currentActiveLinkId: currentActiveLinkId,
      }
      console.log("💾 Saving trip data to localStorage:", dataToSave)
      localStorage.setItem("tripCalendarData_v3", JSON.stringify(dataToSave))

      // Also save to list-specific localStorage if we have a currentListId
      if (currentListId) {
        const listSpecificKey = `tripCalendarData_${currentListId}`
        localStorage.setItem(listSpecificKey, JSON.stringify(dataToSave))
        console.log("💾 Also saved to list-specific key:", listSpecificKey)
      }

      console.log("✅ Trip data saved successfully")
    } catch (e) {
      console.error("❌ Error saving to localStorage:", e)
    }
  }

  // Save whenever data changes
  useEffect(() => {
    if (itinerary.length > 0 || accommodations.length > 0) {
      console.log("🔄 Data changed, saving to localStorage...")
      saveToLocal()
    }
  }, [itinerary, accommodations, calendarName, tripTimezone, sharedPlanId, currentActiveLinkId])

  // Select initial day
  const selectInitialDay = (itineraryData = itinerary) => {
    if (itineraryData.length === 0) {
      console.log("📅 No itinerary to select initial day from")
      return
    }

    const todayStr = formatDateForInput(new Date())
    const todayInTrip = itineraryData.find((day) => day.date === todayStr)

    if (todayInTrip) {
      console.log("📅 Setting selected date to today:", todayStr)
      setSelectedDate(todayStr)
    } else {
      console.log("📅 Setting selected date to first day:", itineraryData[0].date)
      setSelectedDate(itineraryData[0].date)
    }
  }

  // Create new trip
  const createNewTrip = (tripName, startDate, endDate, timezone) => {
    console.log("🆕 Creating new trip:", { tripName, startDate, endDate, timezone })
    setIsLoading(true)
    try {
      const start = new Date(startDate + "T00:00:00")
      const end = new Date(endDate + "T00:00:00")

      if (start > end) {
        alert("Start date must be before or same as end date.")
        setIsLoading(false)
        return
      }

      const newItinerary = []
      const currentDate = new Date(start)
      let placeIndex = 0

      while (currentDate <= end) {
        const dateStr = formatDateForInput(currentDate)
        const dateObj = new Date(currentDate)

        const dayData = {
          date: dateStr,
          displayDate: dateObj.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            weekday: "long",
            timeZone: timezone,
          }),
          plans: [],
        }

        // Add places from voting app as activities (one per day)
        if (places.length > 0 && placeIndex < places.length) {
          const place = places[placeIndex]
          dayData.plans.push({
            type: "activity",
            details: `Visit ${place.name}`,
            time: "14:00",
            confirmed: false,
          })
          placeIndex++
        }

        newItinerary.push(dayData)
        currentDate.setDate(currentDate.getDate() + 1)
      }

      console.log("✅ New itinerary created:", newItinerary)
      setCalendarName(tripName)
      setItinerary(newItinerary)
      setAccommodations([])
      setTripTimezone(timezone)
      setSelectedDate(newItinerary[0]?.date)
      setCurrentMode("edit")
      setShowWelcome(false)
      setShowNewTripModal(false)

      console.log("🎯 Trip created successfully, staying in modal")
    } catch (error) {
      console.error("❌ Error creating trip:", error)
      alert("Error creating trip: " + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Import trip
  const importTrip = (importData) => {
    console.log("📥 Importing trip data...")
    setIsLoading(true)
    try {
      const data = JSON.parse(importData)
      console.log("📥 Parsed import data:", data)

      if (!data.calendarName || !Array.isArray(data.itinerary)) {
        throw new Error("Invalid trip data format")
      }

      setCalendarName(data.calendarName)
      setItinerary(data.itinerary)
      setAccommodations(Array.isArray(data.accommodations) ? data.accommodations : [])
      setTripTimezone(data.tripTimezone || "America/Chicago")
      setSharedPlanId(data.sharedPlanId || null)
      setCurrentActiveLinkId(data.currentActiveLinkId || null)
      setIsSharedPlan(!!data.sharedPlanId)
      setSelectedDate(data.itinerary[0]?.date)
      setCurrentMode("edit")
      setShowWelcome(false)
      setShowImportModal(false)

      console.log("✅ Trip imported successfully, staying in modal")
    } catch (error) {
      console.error("❌ Error importing trip:", error)
      alert("Error importing trip: " + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Add/Edit plan
  const savePlan = (planData) => {
    console.log("💾 Saving plan:", planData)
    const { dayIndex, planIndex, originalDate } = currentEditingPlanInfo
    const { newDate, type, time, details, confirmed } = planData

    if (!newDate || !type || !details) {
      alert("Date, Type, and Details are required.")
      return
    }

    const plan = { type, time, details, confirmed }

    if (planIndex === -1) {
      // Adding new plan
      const targetDayIndex = itinerary.findIndex((d) => d.date === newDate)
      if (targetDayIndex === -1) {
        alert("Cannot add plan to a non-existent day. Please add the day first or select an existing day.")
        return
      }

      const newItinerary = [...itinerary]
      newItinerary[targetDayIndex].plans.push(plan)
      newItinerary[targetDayIndex].plans.sort((a, b) => {
        if (a.type === "accommodation" && b.type !== "accommodation") return -1
        if (a.type !== "accommodation" && b.type === "accommodation") return 1
        return (a.time || "23:59").localeCompare(b.time || "23:59")
      })
      setItinerary(newItinerary)
      console.log("✅ New plan added")
    } else {
      // Editing existing plan
      const newItinerary = [...itinerary]
      newItinerary[dayIndex].plans[planIndex] = plan
      newItinerary[dayIndex].plans.sort((a, b) => {
        if (a.type === "accommodation" && b.type !== "accommodation") return -1
        if (a.type !== "accommodation" && b.type === "accommodation") return 1
        return (a.time || "23:59").localeCompare(b.time || "23:59")
      })
      setItinerary(newItinerary)
      console.log("✅ Plan updated")
    }

    setShowPlanModal(false)
    setCurrentEditingPlanInfo({ originalDate: null, dayIndex: -1, planIndex: -1 })
  }

  // Delete plan
  const deletePlan = () => {
    console.log("🗑️ Deleting plan...")
    setConfirmAction(() => () => {
      const { dayIndex, planIndex } = currentEditingPlanInfo
      const newItinerary = [...itinerary]
      newItinerary[dayIndex].plans.splice(planIndex, 1)
      setItinerary(newItinerary)
      setShowPlanModal(false)
      setCurrentEditingPlanInfo({ originalDate: null, dayIndex: -1, planIndex: -1 })
      setShowConfirmDialog(false)
      console.log("✅ Plan deleted")
    })
    setShowConfirmDialog(true)
  }

  // Open plan modal for editing
  const openPlanModal = (dayIndex = -1, planIndex = -1, date = selectedDate) => {
    console.log("📝 Opening plan modal:", { dayIndex, planIndex, date })
    setCurrentEditingPlanInfo({
      originalDate: date,
      dayIndex,
      planIndex,
    })
    setShowPlanModal(true)
  }

  // Handle close with logging
  const handleClose = () => {
    console.log("❌ Planning modal close requested")
    try {
      onClose()
      console.log("✅ Planning modal closed successfully")
    } catch (error) {
      console.error("❌ Error closing planning modal:", error)
    }
  }

  // Handle switching to planning view
  const handleSwitchToPlanning = () => {
    console.log("🎯 Switching to planning view from modal")
    const tripData = {
      calendarName,
      itinerary,
      accommodations,
      tripTimezone,
      sharedPlanId,
      currentActiveLinkId,
    }

    onClose()
    if (onTripCreated) {
      onTripCreated(tripData)
    }
  }

  if (!isOpen) {
    console.log("🚫 Planning modal not open, returning null")
    return null
  }

  console.log("🎯 Rendering PlanningModal content")

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal">
        {/* Header */}
        <div className="planning-header">
          <div className="planning-title">
            <Calendar size={24} />
            <h2>{calendarName}</h2>
          </div>
          <div className="header-actions">
            {!showWelcome && (
              <>
                <button
                  className={`mode-btn ${currentMode === "view" ? "active" : ""}`}
                  onClick={() => {
                    console.log("👁️ Switching to view mode")
                    setCurrentMode("view")
                  }}
                  title="View Mode"
                >
                  👁️
                </button>
                <button
                  className={`mode-btn ${currentMode === "edit" ? "active" : ""}`}
                  onClick={() => {
                    console.log("✏️ Switching to edit mode")
                    setCurrentMode("edit")
                  }}
                  disabled={!canEdit}
                  title="Edit Mode"
                >
                  <Edit3 size={16} />
                </button>
                <button
                  onClick={() => {
                    console.log("📤 Opening share modal")
                    setShowShareModal(true)
                  }}
                  className="action-btn"
                  title="Share"
                >
                  <Share2 size={16} />
                </button>
                {itinerary.length > 0 && (
                  <button
                    onClick={handleSwitchToPlanning}
                    className="action-btn"
                    title="Open Full Planning View"
                    style={{ background: "#10b981", color: "white" }}
                  >
                    📅
                  </button>
                )}
              </>
            )}
            <button onClick={handleClose} className="close-btn">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="planning-content">
          {showWelcome ? (
            /* Welcome Screen */
            <div className="welcome-container">
              <div className="welcome-icon">
                <Calendar size={64} />
              </div>
              <h2 className="welcome-title">Welcome to Triptoi!</h2>
              <p className="welcome-text">
                Plan your perfect trip with our easy-to-use trip calendar. Add days, create plans, and share your
                itinerary with friends.
              </p>
              <div className="welcome-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    console.log("🆕 Opening new trip modal")
                    setShowNewTripModal(true)
                  }}
                >
                  Create New Trip
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    console.log("📥 Opening import modal")
                    setShowImportModal(true)
                  }}
                >
                  Import Existing Trip
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile Date Carousel */}
              <div className="mobile-date-carousel-container">
                <div className="mobile-date-carousel">
                  {itinerary.map((day, index) => (
                    <div
                      key={day.date}
                      className={`carousel-day-item ${selectedDate === day.date ? "selected" : ""}`}
                      onClick={() => {
                        console.log("📅 Selected date:", day.date)
                        setSelectedDate(day.date)
                      }}
                    >
                      <div className="carousel-day-date">
                        {new Date(day.date + "T12:00:00").toLocaleDateString("en-US", {
                          weekday: "short",
                          timeZone: tripTimezone,
                        })}
                      </div>
                      <div className="carousel-day-number">{new Date(day.date + "T12:00:00").getDate()}</div>
                      <div className="carousel-day-plans">{day.plans.length} plans</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected Day Details */}
              {selectedDate && (
                <div className="selected-day-details-container">
                  {(() => {
                    const day = itinerary.find((d) => d.date === selectedDate)
                    if (!day) return null

                    return (
                      <div className="selected-day-card">
                        <div className="selected-day-header">
                          <div className="selected-day-title">{day.displayDate}</div>
                          <div className="selected-day-subtitle">
                            {day.plans.length} {day.plans.length === 1 ? "plan" : "plans"}
                          </div>
                        </div>
                        <div className="selected-day-content">
                          {day.plans.length === 0 ? (
                            <div className="no-plans">
                              <p>No plans for this day yet.</p>
                              {currentMode === "edit" && (
                                <button
                                  className="btn btn-primary"
                                  onClick={() => {
                                    console.log("➕ Adding first plan for date:", selectedDate)
                                    openPlanModal(-1, -1, selectedDate)
                                  }}
                                >
                                  <Plus size={16} />
                                  Add your first plan
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="plans-list">
                              {day.plans.map((plan, planIndex) => (
                                <div
                                  key={planIndex}
                                  className="plan-item"
                                  onClick={() => {
                                    if (currentMode === "edit") {
                                      console.log("✏️ Editing plan:", { planIndex, plan })
                                      openPlanModal(
                                        itinerary.findIndex((d) => d.date === selectedDate),
                                        planIndex,
                                        selectedDate,
                                      )
                                    }
                                  }}
                                >
                                  <div className="plan-icon">{PLAN_ICONS[plan.type] || "📌"}</div>
                                  <div className="plan-content">
                                    <div className="plan-time">{plan.time || "All Day"}</div>
                                    <div className="plan-details">{plan.details}</div>
                                    <div className="plan-status">{plan.confirmed ? "✅ Confirmed" : "⚠️ Tentative"}</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {currentMode === "edit" && day.plans.length > 0 && (
                            <div className="add-plan-buttons">
                              <button
                                className="btn btn-outline"
                                onClick={() => {
                                  console.log("➕ Adding new plan for date:", selectedDate)
                                  openPlanModal(-1, -1, selectedDate)
                                }}
                              >
                                <Plus size={16} />
                                Add Plan
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </>
          )}
        </div>

        {/* Modals */}
        {showNewTripModal && <NewTripModal />}
        {showImportModal && <ImportModal />}
        {showPlanModal && <PlanModal />}
        {showShareModal && <ShareModal />}
        {showConfirmDialog && <ConfirmDialog />}
      </div>
    </div>
  )

  // New Trip Modal Component
  function NewTripModal() {
    console.log("🆕 Rendering NewTripModal")
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>Create New Trip</h3>
            <button
              onClick={() => {
                console.log("❌ Closing new trip modal")
                setShowNewTripModal(false)
              }}
              className="close-btn"
            >
              <X size={20} />
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              console.log("📝 Submitting new trip form")
              const formData = new FormData(e.target)
              createNewTrip(
                formData.get("tripName"),
                formData.get("startDate"),
                formData.get("endDate"),
                formData.get("timezone"),
              )
            }}
          >
            <div className="modal-content">
              <div className="form-group">
                <label>Trip Name:</label>
                <input
                  type="text"
                  name="tripName"
                  required
                  placeholder="My Amazing Trip"
                  defaultValue={places.length > 0 ? `Trip Plan (${places.length} places)` : ""}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Date:</label>
                  <input type="date" name="startDate" required />
                </div>
                <div className="form-group">
                  <label>End Date:</label>
                  <input type="date" name="endDate" required />
                </div>
              </div>
              <div className="form-group">
                <label>Timezone:</label>
                <select name="timezone" defaultValue="America/Chicago">
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                  <option value="Europe/London">London</option>
                  <option value="Europe/Paris">Paris/Amsterdam</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => {
                  console.log("❌ Cancelling new trip")
                  setShowNewTripModal(false)
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="btn btn-primary">
                {isLoading ? "Creating..." : "Create Trip"}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Import Modal Component
  function ImportModal() {
    console.log("📥 Rendering ImportModal")
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>Import Existing Trip</h3>
            <button
              onClick={() => {
                console.log("❌ Closing import modal")
                setShowImportModal(false)
              }}
              className="close-btn"
            >
              <X size={20} />
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              console.log("📥 Submitting import form")
              const formData = new FormData(e.target)
              importTrip(formData.get("importData"))
            }}
          >
            <div className="modal-content">
              <div className="form-group">
                <label>Paste your exported trip data:</label>
                <textarea name="importData" required rows={8} placeholder="Paste your JSON trip data here..." />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => {
                  console.log("❌ Cancelling import")
                  setShowImportModal(false)
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button type="submit" disabled={isLoading} className="btn btn-primary">
                {isLoading ? "Importing..." : "Import Trip"}
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Plan Modal Component
  function PlanModal() {
    console.log("📝 Rendering PlanModal")
    const { dayIndex, planIndex } = currentEditingPlanInfo
    const isEditing = planIndex !== -1
    const existingPlan = isEditing ? itinerary[dayIndex]?.plans[planIndex] : null

    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>{isEditing ? "Edit Plan" : "Add Plan"}</h3>
            <button
              onClick={() => {
                console.log("❌ Closing plan modal")
                setShowPlanModal(false)
              }}
              className="close-btn"
            >
              <X size={20} />
            </button>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              console.log("💾 Submitting plan form")
              const formData = new FormData(e.target)
              savePlan({
                newDate: formData.get("date"),
                type: formData.get("type"),
                time: formData.get("time"),
                details: formData.get("details"),
                confirmed: formData.get("confirmed") === "on",
              })
            }}
          >
            <div className="modal-content">
              <div className="form-group">
                <label>Date:</label>
                <select name="date" required defaultValue={selectedDate}>
                  {itinerary.map((day) => (
                    <option key={day.date} value={day.date}>
                      {day.displayDate}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Type:</label>
                  <select name="type" required defaultValue={existingPlan?.type || "activity"}>
                    <option value="accommodation">🏠 Accommodation</option>
                    <option value="meal">🍽️ Meal</option>
                    <option value="activity">🎯 Activity</option>
                    <option value="flight">✈️ Flight</option>
                    <option value="drive">🚗 Drive</option>
                    <option value="note">📝 Note</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Time:</label>
                  <input type="time" name="time" defaultValue={existingPlan?.time || "10:00"} />
                </div>
              </div>
              <div className="form-group">
                <label>Details:</label>
                <textarea
                  name="details"
                  required
                  rows={3}
                  placeholder="Enter plan details..."
                  defaultValue={existingPlan?.details || ""}
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input type="checkbox" name="confirmed" defaultChecked={existingPlan?.confirmed || false} />
                  Confirmed
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <div className="modal-footer-left">
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => {
                      console.log("🗑️ Delete plan requested")
                      deletePlan()
                    }}
                    className="btn btn-danger"
                  >
                    Delete
                  </button>
                )}
              </div>
              <div className="modal-footer-right">
                <button
                  type="button"
                  onClick={() => {
                    console.log("❌ Cancelling plan edit")
                    setShowPlanModal(false)
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {isEditing ? "Update" : "Add"} Plan
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    )
  }

  // Share Modal Component
  function ShareModal() {
    console.log("📤 Rendering ShareModal")
    const [exportData, setExportData] = useState("")
    const [showExportData, setShowExportData] = useState(false)

    const generateExportData = () => {
      console.log("📤 Exporting trip data")
      const data = {
        calendarName,
        itinerary,
        accommodations,
        tripTimezone,
        sharedPlanId,
        currentActiveLinkId,
      }
      const dataStr = JSON.stringify(data, null, 2)
      setExportData(dataStr)
      setShowExportData(true)
    }

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(exportData)
        alert("✅ Trip data copied to clipboard!")
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea")
        textArea.value = exportData
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand("copy")
        document.body.removeChild(textArea)
        alert("✅ Trip data copied to clipboard!")
      }
    }

    if (showExportData) {
      return (
        <div className="modal-overlay">
          <div className="modal export-modal">
            <div className="modal-header">
              <h3>Export Data</h3>
              <button onClick={() => setShowShareModal(false)} className="close-btn">
                <X size={20} />
              </button>
            </div>
            <div className="modal-content export-content">
              <textarea value={exportData} readOnly className="export-textarea" rows={15} />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowExportData(false)} className="btn btn-secondary">
                Close
              </button>
              <button onClick={copyToClipboard} className="btn btn-primary">
                Copy
              </button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h3>Share & Export</h3>
            <button
              onClick={() => {
                console.log("❌ Closing share modal")
                setShowShareModal(false)
              }}
              className="close-btn"
            >
              <X size={20} />
            </button>
          </div>
          <div className="modal-content">
            <div className="share-options">
              <button onClick={generateExportData} className="btn btn-primary">
                <Download size={16} />
                Export Trip Data
              </button>
              <p className="help-text">Export your trip data to copy and paste elsewhere or import later.</p>
            </div>
          </div>
          <div className="modal-footer">
            <button
              onClick={() => {
                console.log("❌ Closing share modal")
                setShowShareModal(false)
              }}
              className="btn btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Confirm Dialog Component
  function ConfirmDialog() {
    console.log("⚠️ Rendering ConfirmDialog")
    if (!showConfirmDialog) return null

    return (
      <div className="modal-overlay">
        <div className="modal confirm-modal">
          <div className="modal-header">
            <h3>Confirm Delete</h3>
          </div>
          <div className="modal-content">
            <p>Are you sure you want to delete this plan?</p>
          </div>
          <div className="modal-footer">
            <button
              onClick={() => {
                console.log("❌ Cancelling delete")
                setShowConfirmDialog(false)
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                console.log("✅ Confirming delete")
                confirmAction()
              }}
              className="btn btn-danger"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )
  }
}

export default PlanningModal
